// The item, the logbook, and a narrowed slice of its entries, for a read-only
// logbook sub-page (one trip's entries, one year's).
//
// Every such page has the same load to do, and it is not the obvious one:
//
//   - seed item / logbook from router state for an instant first paint;
//   - paint handed-over entries too, but ONLY when no write is still in flight
//     for them -- the list page autosaves, so it can navigate here with a save
//     in the queue, and the snapshot it handed over was taken before that
//     write. Painting it shows the user their edit "not saved";
//   - always fetch afterwards, which is what lets the check above be a
//     first-paint decision rather than a freshness proof. (An entry's own page
//     can't lean on that, which is why it reads its lane before its first
//     await.)
//
// `select(allEntries, logbook)` narrows the fetched logbook to the page's
// slice. It is held in a ref and the effect keys on `selectKey` instead -- a
// callback in the dep array would refetch on every render.
//
// The WHOLE logbook comes back too (`allEntries`). It is already in hand -- the
// fetch below reads it and the selector throws the rest away -- and a page that
// has to know what OTHER trips exist (LogbookTrip's Combine buttons, its rename
// collision warning) would otherwise pay for a second read of the same table.
// It is null until the fetch lands, including on a handover: router state
// carries the page's own slice, not the logbook, so there is nothing to seed it
// with and neighbours simply appear when the read does.

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useStatusMessage } from '../../components';
import * as Database from '../../utilities/Database.mjs';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { entryLane } from '../../utilities/saveLanes';

export function useLogbookPageEntries({ itemId, logbookId, selectKey, select }) {
  const location = useLocation();

  const [StatusMessage, { showLoading, showError, hideStatus }] = useStatusMessage();
  const [item, setItem] = useState(null);
  const [logbook, setLogbook] = useState(null);
  const [entries, setEntries] = useState(null);
  const [allEntries, setAllEntries] = useState(null);

  const selectRef = useRef(select);
  selectRef.current = select;

  useEffect(() => {
    let mounted = true;
    // Lets the lane wait below be dropped on unmount instead of holding a
    // queue subscription until (or unless) the write finishes.
    const leaving = new AbortController();

    async function load() {
      try {
        // Seed from router state for an instant first paint...
        const stateItem = location.state?.item?.id === itemId ? location.state.item : null;
        const stateLogbook = location.state?.logbook?.id === logbookId ? location.state.logbook : null;
        if (stateItem) setItem(stateItem);
        if (stateLogbook) setLogbook(stateLogbook);

        // ...but only when those entries are actually current (see the header).
        const handedOver = location.state?.entries ?? [];
        const lanes = handedOver.map((entry) => entryLane(entry.id));
        const writesPending = lanes.some((lane) => saveQueue.isPending(lane));
        if (handedOver.length > 0 && !writesPending) setEntries(handedOver);

        if (writesPending) {
          // Say so while we wait: skipping the instant paint already costs the
          // user the only thing on screen, and `isPending` counts a lane
          // sitting in retry backoff -- a save being retried over a flaky
          // connection can hold this for several seconds, and an empty page
          // with no explanation reads as "there is nothing here".
          showLoading('Finishing saved changes...');
          await saveQueue.settledAll(lanes, { signal: leaving.signal });
          if (!mounted) return;
        }

        const [loadedItem, loadedLogbook, loaded] = await Promise.all([
          stateItem ? Promise.resolve(stateItem) : Database.retrieveItem(itemId),
          stateLogbook ? Promise.resolve(stateLogbook) : Database.retrieveLogbook(logbookId),
          Database.listCompletedTasksForLogbook(logbookId),
        ]);
        if (!mounted) return;

        setItem(loadedItem);
        setLogbook(loadedLogbook);
        setAllEntries(loaded);
        setEntries(selectRef.current(loaded, loadedLogbook));
        hideStatus();
      } catch (err) {
        if (mounted) showError(err);
      }
    }

    load();
    return () => { mounted = false; leaving.abort(); };
    // location.state is read once on mount; a different slice means a different
    // URL, so these three are enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, logbookId, selectKey]);

  // The two setters are exposed for the callers that WRITE these rows: the trip
  // page's rename and its combine both touch every entry at once, and the rows
  // they get back from AppSync are newer than anything a refetch would race
  // for. Both copies have to be updated together -- a writer that refreshed
  // only `entries` would leave `allEntries`, and so the Combine buttons, still
  // describing the trips as they were before the write.
  return { item, logbook, entries, setEntries, allEntries, setAllEntries, StatusMessage };
}

export default useLogbookPageEntries;
